import { dispatchAction, connect } from '@ardoq/rxbeach';
import { toggleLegend } from 'presentation/viewPane/actions';
import { map } from 'rxjs/operators';
import { currentViewId$ } from 'streams/currentViewId$';
import { viewHasLegend } from 'views/metaInfoTabs';
import {
  APISlideAttributes,
  ArdoqId,
  EnhancedPresentation,
  ViewIds,
  isVisualizationSlide,
} from '@ardoq/api-types';
import { loadedPresentation$ } from 'presentation/streams/loadedPresentation$';
import { trackToggledExploreMode } from 'presentation/tracking';
import { legendActive$ } from 'presentation/streams/slideLegend$';
import { toggleExploreMode } from 'presentation/actions';
import { combineLatest } from 'rxjs';
import { viewPane$ } from 'presentation/viewPane/viewPane$';
import { Box, FlexBox } from '@ardoq/layout';
import { getScenario } from 'presentation/presentationData';
import { LogoLink, ScenarioChip } from './atoms';
import { GhostButton, IconButton } from '@ardoq/button';
import { IconName, SvgImage, SvgImageName } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { Text } from '@ardoq/typography';
import { t, type I18nLocale } from '@ardoq/i18n';
import { slideOperations } from 'streams/slides/slideOperations';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { featureContext$ } from 'streams/featureContext$';
import { type FeatureContext, featureOperations } from '@ardoq/features';
import { PRESENTATION_CHROME_CLASS } from 'presentation/chrome';
import { getScenarioLabel } from './scenarioLabel';
import { slideNavigator$ } from 'presentation/slideNavigator/slideNavigator$';
import {
  getSelectedSlideTitle,
  presentationSlides$,
} from 'presentation/slideNavigator/presentationSlides$';
import { Slide } from 'presentation/slideNavigator/types';
import { withBasePath } from 'basePath';
import { useFullscreenElement } from '@ardoq/hooks';
import { ContextPicker } from 'presentation/contextSwitch/ContextPicker';
import { PptxExportButton } from 'presentation/pptxExport/PptxExportButton';

const toggleFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen();
    return;
  }
  const container = document.querySelector('#root');
  if (container) {
    container.requestFullscreen();
  }
};

type TopBarProps = {
  slideTitle: string;
  scenarioLabel: string | null;
  canToggleExploreMode: boolean;
  isExploreMode: boolean;
  hasLegend: boolean;
  legendActive: boolean;
  viewId?: ViewIds;
  i18nLocale: I18nLocale;
};

const TopBar = ({
  slideTitle,
  scenarioLabel,
  canToggleExploreMode,
  isExploreMode,
  hasLegend,
  legendActive,
  viewId,
  i18nLocale,
}: TopBarProps) => {
  const isFullscreenActive = Boolean(useFullscreenElement());

  const toggleExploreModeLabel = t(
    i18nLocale,
    'presentationAdmin:viewer.topBar.toggleExploreMode',
    'Toggle explore mode'
  );

  const setIsExploreMode = (nextIsExploreMode: boolean) => {
    dispatchAction(toggleExploreMode({ isExploreMode: nextIsExploreMode }));
    trackToggledExploreMode(nextIsExploreMode);
  };

  const toggleSlideLegend = () => {
    if (!viewId) return;
    dispatchAction(toggleLegend({ legendActive: !legendActive, viewId }));
  };

  return (
    <FlexBox
      as="header"
      className={PRESENTATION_CHROME_CLASS}
      align="center"
      gap="medium"
      width="full"
      flexShrink={0}
      paddingX="medium"
      paddingY="small"
      backgroundColor="surfaceDefaultSubtle"
      borderWidthBottom={1}
      borderColor="borderSubtle"
    >
      <FlexBox align="center" gap="small" flex={1}>
        <LogoLink
          href={withBasePath('/app/home')}
          target="_self"
          aria-label={t(
            i18nLocale,
            'presentationAdmin:viewer.topBar.goToArdoq',
            'Go to Ardoq'
          )}
        >
          <SvgImage
            svgImageName={SvgImageName.ARDOQ_LOGO_MONO_WITH_NAME}
            width={90}
            height={26}
            color={colors.iconStrong}
          />
        </LogoLink>
        {scenarioLabel && <ScenarioChip>{scenarioLabel}</ScenarioChip>}
      </FlexBox>

      <Box minWidth="0" role="heading" aria-level={1} aria-live="polite">
        <Text variant="text1Bold" color="textModerate" align="center" truncate>
          {slideTitle}
        </Text>
      </Box>

      <FlexBox align="center" justify="flex-end" gap="small" flex={1}>
        <ContextPicker />

        {canToggleExploreMode && (
          <GhostButton
            isActive={isExploreMode}
            onClick={() => setIsExploreMode(!isExploreMode)}
            aria-label={toggleExploreModeLabel}
            data-tooltip-text={toggleExploreModeLabel}
          >
            {t(
              i18nLocale,
              'presentationAdmin:viewer.topBar.exploreMode',
              'Explore'
            )}
          </GhostButton>
        )}

        <PptxExportButton i18nLocale={i18nLocale} />

        {hasLegend && (
          <IconButton
            isActive={legendActive}
            iconName={IconName.INFO}
            label={t(
              i18nLocale,
              'presentationAdmin:viewer.topBar.legend',
              'Toggle legend'
            )}
            onClick={toggleSlideLegend}
          />
        )}

        {document.fullscreenEnabled && (
          <IconButton
            isActive={isFullscreenActive}
            iconName={
              isFullscreenActive
                ? IconName.FULLSCREEN_EXIT
                : IconName.FULLSCREEN
            }
            label={
              isFullscreenActive
                ? t(
                    i18nLocale,
                    'presentationAdmin:viewer.topBar.exitFullscreen',
                    'Exit fullscreen'
                  )
                : t(
                    i18nLocale,
                    'presentationAdmin:viewer.topBar.fullscreen',
                    'Fullscreen'
                  )
            }
            onClick={toggleFullscreen}
          />
        )}
      </FlexBox>
    </FlexBox>
  );
};

type ToStreamPropsArgs = {
  presentation: EnhancedPresentation | null;
  slide: APISlideAttributes | null;
  slides: Slide[];
  selectedSlideId: ArdoqId | null;
  viewId: ViewIds | undefined;
  legendActive: boolean;
  isExploreMode: boolean;
  i18nLocale: I18nLocale;
  featureContext: FeatureContext;
};

const toStreamProps = ({
  presentation,
  slide,
  slides,
  selectedSlideId,
  viewId,
  legendActive,
  isExploreMode,
  i18nLocale,
  featureContext,
}: ToStreamPropsArgs): TopBarProps => {
  // The slide is undefined if the user has no permission, then do not show
  // scenario mode label.
  const visualizationSlide =
    slide && isVisualizationSlide(slide) ? slide : undefined;
  const isVizSlide = visualizationSlide !== undefined;
  const scenarioId = visualizationSlide?.scenarioId || undefined;
  const isViewpointSlide =
    visualizationSlide !== undefined &&
    slideOperations.isViewpointSlide(visualizationSlide);
  const scenarioName =
    (scenarioId && getScenario(presentation, scenarioId)?.name) || undefined;
  return {
    slideTitle: getSelectedSlideTitle(slides, selectedSlideId),
    scenarioLabel: getScenarioLabel({ scenarioName, i18nLocale }),
    canToggleExploreMode:
      !(presentation?.lockedContext ?? false) &&
      !featureOperations.isHardNaxEnabled(featureContext) &&
      isVizSlide &&
      !isViewpointSlide,
    isExploreMode,
    hasLegend: Boolean(viewId && viewHasLegend(viewId) && isVizSlide),
    legendActive,
    viewId,
    i18nLocale,
  };
};

export const topBar = connect(
  TopBar,
  combineLatest([
    loadedPresentation$,
    viewPane$,
    currentViewId$,
    legendActive$,
    slideNavigator$,
    presentationSlides$,
    i18nLocale$,
    featureContext$,
  ]).pipe(
    map(
      ([
        { presentation },
        { loadedSlide },
        viewId,
        legendActive,
        { isExploreMode, selectedSlideId },
        slides,
        i18nLocale,
        featureContext,
      ]) =>
        toStreamProps({
          presentation,
          slide: loadedSlide,
          slides,
          selectedSlideId,
          viewId,
          legendActive,
          isExploreMode,
          i18nLocale,
          featureContext,
        })
    )
  )
);
